/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { inject, Injectable, signal } from '@angular/core';
import { AppConfigService } from '@alfresco/adf-core';
import { AlfrescoApiService } from '@alfresco/adf-content-services';
import { NodesApi } from '@alfresco/js-api';
import { from, Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { AcaConfigFolderService } from '../shared/aca-config-folder.service';

/** The watermark's shape, as the administrator saved it. */
export interface WatermarkConfig {
  /** Master switch — when off nothing is drawn and print goes out unstamped. */
  enabled: boolean;
  /** Free text; `{{userName}}`, `{{userEmail}}`, `{{dateTime}}` and `{{docName}}` are replaced when drawn. */
  text: string;
  /** A data URI, or an empty string for a text-only mark. */
  logo: string;
  /** `#rrggbb`. */
  color: string;
  /** 0.05 – 0.6; how strongly the mark shows through the page. */
  opacity: number;
  fontSize: number;
  /** Logo height as a percentage of its base size (1.8 × the text size). */
  logoScale: number;
  /** Degrees; a negative angle climbs to the right, like most "CONFIDENTIAL" marks. */
  rotation: number;
  /** `tiled` repeats the mark across the whole page; `center` draws it once in the middle. */
  layout: 'tiled' | 'center';
  /** Whether copies sent to the printer carry the mark. */
  print: boolean;
}

/** Who is looking — the values `{{userName}}`/`{{userEmail}}` resolve to. */
export interface WatermarkViewer {
  name: string;
  email: string;
}

/**
 * The document watermark, as an administrator set it.
 *
 * The configuration is kept where branding already keeps its own: one small
 * document in the repository — `Data Dictionary/ACA/watermark.json` — which
 * every signed-in user can read (Data Dictionary grants Consumer to
 * GROUP_EVERYONE) and only an administrator can write. That makes the mark a
 * system-wide policy rather than a per-user preference: the administrator
 * decides once, on the settings page, and every reader of every document sees
 * it the same way.
 *
 * The mark is only ever drawn on top of what is shown — the repository's
 * binary is never touched, and a downloaded file stays exactly as stored.
 * Printed copies are the exception: they are stamped on the fly so the mark
 * reaches paper (see WatermarkPrintService).
 */
@Injectable({ providedIn: 'root' })
export class WatermarkService {
  private readonly api = inject(AlfrescoApiService);
  private readonly configFolder = inject(AcaConfigFolderService);
  private readonly config = inject(AppConfigService);

  private static readonly FOLDER_PATH = 'Data Dictionary/ACA';
  private static readonly FILE_NAME = 'watermark.json';

  /** Above this a logo is refused: it travels inside the JSON document. */
  static readonly MAX_LOGO_BYTES = 512 * 1024;

  /** What a deployment that never configured a watermark shows: nothing. */
  static readonly DEFAULT: WatermarkConfig = {
    enabled: false,
    text: '{{userName}} · {{dateTime}}',
    logo: '',
    color: '#475569',
    opacity: 0.16,
    fontSize: 22,
    logoScale: 100,
    rotation: -30,
    layout: 'tiled',
    print: true
  };

  static readonly OPACITY_MIN = 0.05;
  static readonly OPACITY_MAX = 0.6;
  static readonly FONT_MIN = 10;
  static readonly FONT_MAX = 96;
  static readonly LOGO_SCALE_MIN = 25;
  static readonly LOGO_SCALE_MAX = 400;

  readonly settings = signal<WatermarkConfig>(WatermarkService.DEFAULT);

  /**
   * The signed-in person, for the `{{userName}}`/`{{userEmail}}` marks.
   * Empty until `load()` has run — before sign-in the repository cannot be
   * asked, and the mark simply shows the placeholders resolved against no one.
   */
  readonly viewer = signal<WatermarkViewer>({ name: '', email: '' });

  private loaded = false;

  /** Reads the configuration and the current person once per session. */
  load(): void {
    if (this.loaded) {
      return;
    }
    this.loaded = true;

    this.read()
      .pipe(catchError(() => of(null)))
      .subscribe((saved) => {
        if (saved) {
          this.settings.set(saved);
        }
      });

    const instance: any = this.api.getInstance();
    from(instance.contentClient.callApi('/people/-me-', 'GET', {}, {}, {}, {}, null, ['application/json'], ['application/json']) as Promise<any>)
      .pipe(
        map((response: any) => response?.entry),
        catchError(() => of(null))
      )
      .subscribe((entry: any) => {
        if (entry) {
          this.viewer.set({
            name: entry.displayName || [entry.firstName, entry.lastName].filter(Boolean).join(' ') || entry.id,
            email: entry.email || ''
          });
        }
      });
  }

  /** Writes the configuration for everyone. Requires an administrator. */
  save(settings: WatermarkConfig): Observable<void> {
    const clean = WatermarkService.sanitize(settings);
    return this.nodeId(true).pipe(
      switchMap((nodeId) => from(this.nodes().updateNodeContent(nodeId, JSON.stringify(clean)) as Promise<any>)),
      tap(() => this.settings.set(clean)),
      map(() => undefined)
    );
  }

  /**
   * Fills the `{{placeholders}}` of a template. Unknown names are left as they
   * are typed, so a mistyped one shows up in the settings preview instead of
   * disappearing silently.
   */
  resolve(template: string, docName = '', when: Date = new Date()): string {
    const viewer = this.viewer();
    const values: Record<string, string> = {
      userName: viewer.name,
      userEmail: viewer.email,
      docName,
      dateTime: this.formatDateTime(when)
    };
    return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (raw, key) => values[key] ?? raw);
  }

  /** `{{dateTime}}` in the reader's own locale — Thai gets its Buddhist year. */
  private formatDateTime(when: Date): string {
    const locale = this.config.get<string>('locale', 'th') || 'th';
    try {
      return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(when);
    } catch {
      return when.toLocaleString();
    }
  }

  /**
   * Trims a configuration into a safe shape: out-of-range numbers are clamped,
   * an unknown layout becomes `tiled`, and a mark with neither text nor logo is
   * turned off — there is nothing to draw.
   */
  static sanitize(settings: WatermarkConfig): WatermarkConfig {
    const clean: WatermarkConfig = {
      enabled: !!settings.enabled,
      text: (settings.text || '').slice(0, 200),
      logo: settings.logo || '',
      color: WatermarkService.isHex(settings.color) ? settings.color : WatermarkService.DEFAULT.color,
      opacity: WatermarkService.clamp(settings.opacity, WatermarkService.OPACITY_MIN, WatermarkService.OPACITY_MAX, WatermarkService.DEFAULT.opacity),
      fontSize: WatermarkService.clamp(settings.fontSize, WatermarkService.FONT_MIN, WatermarkService.FONT_MAX, WatermarkService.DEFAULT.fontSize),
      logoScale: WatermarkService.clamp(
        settings.logoScale,
        WatermarkService.LOGO_SCALE_MIN,
        WatermarkService.LOGO_SCALE_MAX,
        WatermarkService.DEFAULT.logoScale
      ),
      rotation: WatermarkService.clamp(settings.rotation, -90, 90, WatermarkService.DEFAULT.rotation),
      layout: settings.layout === 'center' ? 'center' : 'tiled',
      print: settings.print !== false
    };
    if (!clean.text.trim() && !clean.logo) {
      clean.enabled = false;
    }
    return clean;
  }

  private static clamp(value: number, min: number, max: number, fallback: number): number {
    const n = Number(value);
    if (!isFinite(n)) {
      return fallback;
    }
    return Math.min(max, Math.max(min, n));
  }

  static isHex(value: string): boolean {
    return /^#[0-9a-fA-F]{6}$/.test(value);
  }

  private read(): Observable<WatermarkConfig | null> {
    return this.nodeId(false).pipe(
      switchMap((nodeId) => (nodeId ? from(this.nodes().getNodeContent(nodeId) as Promise<any>) : of(null))),
      switchMap((content: any) => (content ? this.text(content) : of(null))),
      map((text) => {
        if (!text) {
          return null;
        }
        try {
          return WatermarkService.sanitize({ ...WatermarkService.DEFAULT, ...(JSON.parse(text) as WatermarkConfig) });
        } catch {
          // a hand-edited or truncated document must not break the page
          return null;
        }
      })
    );
  }

  /** A Blob on the browser, but the API's typings promise only `any`. */
  private text(content: any): Observable<string | null> {
    if (typeof content === 'string') {
      return of(content);
    }
    if (content instanceof Blob) {
      return from(content.text());
    }
    return of(null);
  }

  /**
   * The document's node id, creating the folder and the document on the way
   * when `create` is set — which only ever happens on an administrator's save.
   */
  private nodeId(create: boolean): Observable<string | null> {
    const nodes = this.nodes();

    // listed once for every settings document — see AcaConfigFolderService
    return this.configFolder.find(WatermarkService.FILE_NAME).pipe(
      catchError(() => {
        if (!create) {
          return of(null);
        }

        // the folder may or may not be there; either way the document is not
        return from(nodes.createNode('-root-', { name: 'ACA', nodeType: 'cm:folder', relativePath: 'Data Dictionary' } as any) as Promise<any>).pipe(
          catchError(() => of(null)),
          switchMap(() =>
            from(
              nodes.createNode('-root-', {
                name: WatermarkService.FILE_NAME,
                nodeType: 'cm:content',
                relativePath: WatermarkService.FOLDER_PATH
              } as any) as Promise<any>
            )
          ),
          map((entry: any) => entry?.entry?.id as string),
          tap(() => this.configFolder.forget())
        );
      })
    );
  }

  private nodes(): NodesApi {
    return new NodesApi(this.api.getInstance() as any);
  }
}
